import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_ModelFunctionRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbModelFunction} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Model_Function
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {ConstraintBasedOptimizerBOValidation} from "../PA.Simulation.BusinessObjects/ConstraintBasedOptimizerBOValidation";
import {ModelFunctionBOValidations} from "./ModelFunctionBO.Validations";
import { LeaseRegistry } from '../PA.Utils/LeaseRegistry';


export class ModelFunctionBO implements IBusinessObject
{
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelFunction: WB_ModelFunctionRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Model Function'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ModelFunction = null;
    }

    get WB_ModelFunction(): WB_ModelFunctionRow {
        return this.drWB_ModelFunction;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Model Function");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                let sError = "Unable to determine dbContext for New Model Function.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Model_Function)
                .then((newModelFunctionRow: WB_ModelFunctionRow)=>{
                    newModelFunctionRow.setDefaults();
                    self.drWB_ModelFunction = newModelFunctionRow;
                    self.dbContext.add(esWB_Model_Function, newModelFunctionRow);
                    self.Identity.setAll(self.WB_ModelFunction.cif.WB_MODEL_FUNCTION_ID, self.WB_ModelFunction.cif.MODEL_FUNCTION_ID, self.WB_ModelFunction.cif.MODEL_FUNCTION_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ModelFunction, 'WB_MODEL_FUNCTION_ID','MODEL_FUNCTION_ID','MODEL_FUNCTION_NAME');

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            if (self.bIsLoaded) {
                let sError = "Model Function already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Model Function.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ModelFunctionBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ModelFunctionBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newModelFunctionRow: any = recordResolver.get(esWB_Model_Function, anId);

                    if(_.isEmpty(newModelFunctionRow))
                    {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ModelFunction = newModelFunctionRow;
                    self.Identity.setAll(self.WB_ModelFunction.cif.WB_MODEL_FUNCTION_ID, self.WB_ModelFunction.cif.MODEL_FUNCTION_ID, self.WB_ModelFunction.cif.MODEL_FUNCTION_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ModelFunction, 'WB_MODEL_FUNCTION_ID','MODEL_FUNCTION_ID','MODEL_FUNCTION_NAME');
                })
                .then((newModelFunctionRow:any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err:any)=>{
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_ModelFunction.cif.MODEL_FUNCTION_NAME + "(" + self.WB_ModelFunction.cif.MODEL_FUNCTION_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate Model Function.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            ModelFunctionBOValidations.ValidateFields(self)
                .then((ignore) => {
                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbModelFunction = <IwbModelFunction>self.drWB_ModelFunction;
        this.SaveProgressProvider.start("Saving " + wbModelFunction.MODEL_FUNCTION_NAME + "(" + wbModelFunction.MODEL_FUNCTION_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // We could search the tree if there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Model Function";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }
            let bDone = false
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                    return saveFunction(esWB_Model_Function, self.drWB_ModelFunction, {})
                        .then((ignored)=>{
                            if (bBatch) {
                                self.dbContext.addBatchCompleteListener(()=> {
                                    self._bIsSaved = true;
                                    LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)));
                                })
                            } else {
                                self._bIsSaved = true;
                                LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)));
                            }
                            self.SaveProgressProvider.finish();
                            resolve(self);
                        })
                        .catch((err) => {
                            self.SaveProgressProvider.exception(err);
                            reject(self);
                        })
                })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModelFunction = <IwbModelFunction>self.drWB_ModelFunction;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbModelFunction.MODEL_FUNCTION_NAME + "(" + wbModelFunction.MODEL_FUNCTION_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Model Function

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ModelFunction.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const model_function_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, model_function_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Model_Function,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( ModelFunctionBO , "ModelFunctionBO", "Model Function")
